// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.

/**
 * Header seat chip decision (#64) — pure, no I/O except the one guarded read
 * in {@link readSeatChipFromLocation}.
 *
 * TRUST RULE (binding, #64): the `is-demo-seat` / `is-educational-seat`
 * query-string flags are CLIENT-CONTROLLED. A user can edit the URL and hide
 * or fake a chip. They are an UNTRUSTED DISPLAY HINT only — never an
 * authorization, restriction, routing or audit signal. Nothing in this widget,
 * or anywhere else in AU0009, may enable, disable, limit or route anything
 * based on them. Any real restriction for demo or educational seats must be
 * enforced server-side, in its own issue, behind ZC-native auth and the
 * #60 / #62 seat shield. Spoofing a flag changes only what the user sees
 * about themselves.
 *
 * The widget reads the flags ONCE on mount and holds the result as one
 * session-level value in `App`. It never passes through
 * `features/orchestrator.ts`, never appears in per-stage progress (ADR-007
 * D5) and never affects a query's halt-and-report (ADR-006).
 *
 * This is a USER fact. It is independent of the DEVICE `is_demo` of ADR-008
 * (the device lives in the demo registry); neither is derived from the other.
 */

/** The chip icons, both drawn for this work (see `SeatChip`). */
export type SeatChipIcon = "office-chair" | "graduation-cap";

/**
 * The tone colour of the chip's border and icon (never its text):
 * `warning` = `--color-nc-warning`, reserved for Demo (limits may apply);
 * `secondary` = `--nc-color-secondary-1`, a context rather than a warning.
 * The info blue (`--nc-color-feedback-4`) is deliberately not used, so a seat
 * chip never shares a colour with the ADR-008 "Demo device" indication.
 */
export type SeatChipTone = "warning" | "secondary";

export interface SeatChipSpec {
  readonly label: string;
  readonly icon: SeatChipIcon;
  readonly tone: SeatChipTone;
  /**
   * Supplementary context only (#67, parity with AU0008 #79): the label alone
   * must still carry the chip's full meaning. Optional — a spec without it
   * renders the plain, non-focusable pill.
   */
  readonly tooltip?: string;
}

// Display-only. Fed by untrusted query-string flags (see #64 "Trust rule").
// Never use these keys for authorization, restriction or routing. The
// tooltips are display-only text chosen by the same untrusted flags: like the
// chip itself, they enable, limit or route nothing.
// The title casing of the labels is a deliberate PM choice (product terms
// matching the seat concept), an intentional exception to sentence case.
// The tooltip copy is verbatim from AU0008 (`updaterCopy.ts`), so both AUs
// say the same thing about the same seat (#67).
export const SEAT_CHIPS = {
  demo: {
    label: "Demo Seat",
    icon: "office-chair",
    tone: "warning",
    tooltip:
      "Your demo seat includes limited access. Some features may be restricted later, and access may change over time.",
  },
  educational: {
    label: "Educational Seat",
    icon: "graduation-cap",
    tone: "secondary",
    tooltip:
      "Your educational seat is meant for learning and teaching. Some features may be restricted later, and access may change over time.",
  },
} as const satisfies Readonly<Record<string, SeatChipSpec>>;

export type SeatChipKey = keyof typeof SEAT_CHIPS;

/**
 * The query-string keys that carry the seat context (#64). Kebab-case (PM
 * decision), the same spelling as AU0008 (#79), so one host can set the same
 * flags for both AUs.
 */
export const SEAT_QUERY_KEYS = {
  demo: "is-demo-seat",
  educational: "is-educational-seat",
} as const;

/**
 * Only the exact lowercase string "true" counts as true. "false", missing,
 * empty, "TRUE", "1" or any other value counts as false: an invalid value is a
 * defined "no chip" branch, not an error.
 */
function isTrueFlag(params: URLSearchParams, key: string): boolean {
  return params.get(key) === "true";
}

/**
 * The chip for the given query string, or null for none. Never throws.
 * Both flags true shows Demo only: a demo seat implies limits, so it takes
 * precedence, and at most one chip is shown (PM decision, #64).
 */
export function resolveSeatChip(params: URLSearchParams): SeatChipKey | null {
  if (isTrueFlag(params, SEAT_QUERY_KEYS.demo)) return "demo";
  if (isTrueFlag(params, SEAT_QUERY_KEYS.educational)) return "educational";
  return null;
}

/**
 * Reads the widget's own query string and resolves the chip. The widget runs
 * in an external frame with no direct ZOHO.CREATOR SDK (see
 * `bridge/gateway-client.ts`), so the frame's own `location.search` is the
 * query string the host sets. An unreadable query string is a defined
 * "no chip" branch: it is logged to the widget's console diagnostics and
 * never blocks or delays a device query. Never throws.
 */
export function readSeatChipFromLocation(readSearch: () => string): SeatChipKey | null {
  try {
    return resolveSeatChip(new URLSearchParams(readSearch()));
  } catch (error) {
    console.warn("[au0009]", "seat chip: query string unreadable; no chip shown", error);
    return null;
  }
}
